/**
 * Возможности аудио-моделей по ВЕРСИИ (владелец 2026-08-21: «скрывать
 * действия, не поддерживаемые выбранной моделью», включая перекрёстные).
 * Зеркало бэка backend/app/services/music_model_caps.py — источник правды
 * там же (прод-реестр ошибок), здесь только UI-гейты:
 *  • пин трека → в пикере только модели ЕГО провайдера (операция всё равно
 *    уйдёт по провайдеру трека; чужая модель давала Suno «model error»);
 *  • mureka-o2 не принимает персону (vocal_id) — пилюля «Голос» глушится;
 *  • mureka-9.5 недоступна нашему аккаунту Mureka — из пикера скрыта.
 */
import type { Model } from '../../../data/models'
import type { ClipPricing } from '../../../api/music'

export type MusicProvider = 'suno' | 'mureka'

/** Версии Mureka, недоступные аккаунту (API: «not supported for this user»). */
export const MUREKA_UNAVAILABLE: readonly string[] = ['mureka-9.5']
/** Версии Mureka без управляющих vocal_id/melody_id (персона, голос). */
export const MUREKA_NO_PERSONA: readonly string[] = ['mureka-o2']

/** Клип (Mureka /video/generate поверх Seedance): разрешения по модели —
 *  спека 2026-08-21: 2.5 (дефолт) — 480p/720p; 2.0 — до 4k; fast/mini — 480p/720p.
 *  1080p у дефолтной 2.5 = отказ провайдера после списания, поэтому не предлагаем. */
export const CLIP_DEFAULT_MODEL = 'doubao-seedance-2-5-260628'
export const CLIP_MODEL_RESOLUTIONS: Record<string, readonly string[]> = {
	'doubao-seedance-2-5-260628': ['480p', '720p'],
	'doubao-seedance-2-0-260128': ['480p', '720p', '1080p', '4k'],
	'doubao-seedance-2-0-fast-260128': ['480p', '720p'],
	'doubao-seedance-2-0-mini-260615': ['480p', '720p'],
}

export function clipResolutionsFor(model?: string | null): readonly string[] {
	return CLIP_MODEL_RESOLUTIONS[model || CLIP_DEFAULT_MODEL] ?? CLIP_MODEL_RESOLUTIONS[CLIP_DEFAULT_MODEL]
}

/** Модели клипа на выбор юзеру (порядок = порядок пилюль). */
export const CLIP_MODELS: readonly { id: string; label: string; hint: string }[] = [
	{ id: 'doubao-seedance-2-5-260628', label: 'Seedance 2.5', hint: 'до 720p' },
	{ id: 'doubao-seedance-2-0-260128', label: 'Seedance 2.0', hint: 'до 4k' },
	{ id: 'doubao-seedance-2-0-fast-260128', label: '2.0 Fast', hint: 'быстрее' },
	{ id: 'doubao-seedance-2-0-mini-260615', label: '2.0 Mini', hint: 'дешевле' },
]

/** Цена клипа = база × множитель. База (ключ `mureka_clip` в админке) — сцена
 *  «5 с · Seedance 2.5 · 720p». Замер 2026-08-21: $0.29 за секунду, линейно по
 *  длительности. Матрица замеров 2026-08-21 (5 с по тексту): 2.0 ×0.72 от 2.5; 480p ×0.56;
 *  1080p ×2.06 и 4k ×3.99 к 720p той же модели — множители с небольшим запасом над замером.
 *  Таблицы ЗЕРКАЛЬНЫ бэку (music_model_caps.py) — менять синхронно: показ = списание. */
export const CLIP_BASE_SECONDS = 5
export const CLIP_MODEL_PRICE_FACTOR: Record<string, number> = {
	'doubao-seedance-2-5-260628': 1.0,
	'doubao-seedance-2-0-260128': 0.75,
	'doubao-seedance-2-0-fast-260128': 0.55, // замер: $0.75 = ×0.51
	'doubao-seedance-2-0-mini-260615': 0.4, // замер: $0.46–0.52 = ×0.31–0.35
}
export const CLIP_RESOLUTION_PRICE_FACTOR: Record<string, number> = { '480p': 0.6, '720p': 1.0, '1080p': 2.1, '4k': 4.0 }

/** «Губы в такт» с персонажем (стык или фото персонажей): у Mureka этот режим
 *  $0.30 у всех моделей (×0.2) — юзеру скидка ×0.4. Без персонажа губы
 *  игнорируются провайдером — цена полная. */
export const CLIP_LIP_SYNC_PRICE_FACTOR = 0.4

/** Та же таблица, что отдаёт бэк (ClipPricing в api/music), без base_seconds — он фиксирован CLIP_BASE_SECONDS. */
export type ClipPricingTable = Omit<ClipPricing, 'base_seconds'>

/** Дефолты кода; боевые значения приходят с бэка (getClipPricing) — их правит админ. */
export const DEFAULT_CLIP_PRICING: ClipPricingTable = {
	model: CLIP_MODEL_PRICE_FACTOR,
	resolution: CLIP_RESOLUTION_PRICE_FACTOR,
	lip_sync: CLIP_LIP_SYNC_PRICE_FACTOR,
}

/** (длительность / 5 с) × модель × разрешение × губы — порядок умножения как на бэке. */
export function clipPriceMultiplier(
	durationS?: number | null, model?: string | null, resolution?: string | null,
	lipCharacter = false, pricing: ClipPricingTable = DEFAULT_CLIP_PRICING,
): number {
	const d = Math.max(1, Number(durationS) || CLIP_BASE_SECONDS)
	const kModel = pricing.model[model || CLIP_DEFAULT_MODEL] ?? 1
	const kRes = pricing.resolution[(resolution || '720p').toLowerCase()] ?? 1
	const kLip = lipCharacter ? (pricing.lip_sync ?? CLIP_LIP_SYNC_PRICE_FACTOR) : 1
	return (d / CLIP_BASE_SECONDS) * kModel * kRes * kLip
}

/** Цена сцены в кредитах — то же округление «половина вверх», что int(x + 0.5) на бэке. */
export function clipCredits(
	base: number, durationS?: number | null, model?: string | null, resolution?: string | null,
	lipCharacter = false, pricing: ClipPricingTable = DEFAULT_CLIP_PRICING,
): number {
	return Math.floor(base * clipPriceMultiplier(durationS, model, resolution, lipCharacter, pricing) + 0.5)
}

export function musicProviderOf(modelId: string | null | undefined): MusicProvider {
	return (modelId || '').toLowerCase().startsWith('mureka') ? 'mureka' : 'suno'
}

/** Модель вообще можно предлагать (не выключена у аккаунта провайдера). */
export function modelUsable(modelId: string | null | undefined): boolean {
	return !MUREKA_UNAVAILABLE.includes((modelId || '').toLowerCase())
}

/** Версия принимает персону/голос. */
export function modelSupportsPersona(modelId: string | null | undefined): boolean {
	return !MUREKA_NO_PERSONA.includes((modelId || '').toLowerCase())
}

/** Подсказка, почему голос недоступен у этой версии (или null). */
export function personaBlockedReason(modelId: string | null | undefined): string | null {
	if (modelSupportsPersona(modelId)) return null
	return 'Mureka o2 не поддерживает выбор голоса — выбери Mureka 8 или 9'
}

/**
 * Модели для пикера с учётом пина: операция над треком уходит по провайдеру
 * ТРЕКА, поэтому при пине показываем только его провайдера (иначе юзер
 * выбирал Mureka для Suno-файла и ловил «model error»). Всегда без недоступных.
 */
export function pickerModelsForTrack<T extends Pick<Model, 'id' | 'category'>>(
	models: T[],
	sourceTrackModel: string | null | undefined,
	hasTrackOperation: boolean,
): T[] {
	const usable = models.filter((m) => modelUsable(m.id))
	if (!hasTrackOperation || !sourceTrackModel) return usable
	const prov = musicProviderOf(sourceTrackModel)
	const same = usable.filter((m) => m.category !== 'audio' || musicProviderOf(m.id) === prov)
	return same.length ? same : usable
}
